import { useEffect, useState } from 'react';
import salesApiClient from '@/api/salesClient';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3, TrendingUp, Users, Activity, Download, User } from 'lucide-react';
import { toast } from 'sonner';

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || localStorage.getItem('salesUser') || localStorage.getItem('userData') || '{}');

  // C-Level roles have full team visibility
  const cLevelRoles = ['ceo', 'cso', 'director'];
  const isCLevel = cLevelRoles.includes(user.role);
  const canSeeTeam = user.role === 'admin' || user.role === 'manager' || isCLevel || user.role === 'super_admin';

  useEffect(() => {
    fetchStats();
    if (canSeeTeam) {
      fetchTeamMembers();
    }
  }, []);

  const fetchStats = async () => {
    try {
      const response = await salesApiClient.get('/reports/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await salesApiClient.get('/admin/team-members');
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };

  const handleDownloadExcel = async () => {
    setIsDownloading(true);
    try {
      const response = await salesApiClient.get('/admin/export-excel', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `aurous_team_report_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel report downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download report');
      console.error('Download error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!stats) {
    return (
      <div className="p-6 md:p-8 lg:p-12 flex items-center justify-center">
        <p className="text-slate-400">Loading reports...</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-12 space-y-8 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white" data-testid="reports-title">
            Reports & Analytics
          </h1>
          <p className="text-base text-slate-400 mt-2">Team performance and insights</p>
        </div>
        {canSeeTeam && (
          <Button
            onClick={handleDownloadExcel}
            disabled={isDownloading}
            data-testid="download-excel-button"
          >
            <Download size={20} className="mr-2" />
            {isDownloading ? 'Downloading...' : 'Download Excel Report'}
          </Button>
        )}
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="reports-stats-grid">
        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Total Contacts</p>
              <p className="text-3xl font-bold text-white mt-2" data-testid="report-total-contacts">
                {stats.total_contacts}
              </p>
            </div>
            <div className="bg-blue-500 p-3 rounded-lg">
              <Users size={24} className="text-white" strokeWidth={2} />
            </div>
          </div>
        </Card>

        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Total Leads</p>
              <p className="text-3xl font-bold text-white mt-2" data-testid="report-total-leads">
                {stats.total_leads}
              </p>
            </div>
            <div className="bg-green-500 p-3 rounded-lg">
              <TrendingUp size={24} className="text-white" strokeWidth={2} />
            </div>
          </div>
        </Card>

        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Pending Tasks</p>
              <p className="text-3xl font-bold text-white mt-2" data-testid="report-pending-tasks">
                {stats.pending_tasks}
              </p>
            </div>
            <div className="bg-amber-500 p-3 rounded-lg">
              <Activity size={24} className="text-white" strokeWidth={2} />
            </div>
          </div>
        </Card>

        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Pipeline Value</p>
              <p className="text-3xl font-bold text-white mt-2" data-testid="report-pipeline-value">
                ${stats.total_pipeline_value.toLocaleString()}
              </p>
            </div>
            <div className="bg-primary p-3 rounded-lg">
              <BarChart3 size={24} className="text-white" strokeWidth={2} />
            </div>
          </div>
        </Card>
      </div>

      {/* Pipeline Breakdown */}
      <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6" data-testid="pipeline-breakdown">
        <h2 className="text-xl font-semibold text-white mb-6">Pipeline Breakdown</h2>
        <div className="space-y-4">
          {Object.entries(stats.pipeline_stats).map(([stage, count]) => {
            const total = Object.values(stats.pipeline_stats).reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
            
            return (
              <div key={stage}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-300 capitalize" data-testid={`stage-${stage}-label`}>
                    {stage}
                  </span>
                  <span className="text-sm font-mono text-slate-400" data-testid={`stage-${stage}-count`}>
                    {count} leads ({percentage}%)
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${percentage}%` }}
                    data-testid={`stage-${stage}-bar`}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Team Members Overview - Admin/Manager/C-Level Only */}
      {canSeeTeam && teamMembers.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-white mb-4">Team Members Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="team-members-grid">
            {teamMembers.map((member) => (
              <Card
                key={member.user.id}
                className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6"
                data-testid={`team-member-${member.user.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="bg-primary p-3 rounded-lg">
                    <User size={24} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white">{member.user.name}</h3>
                    <p className="text-sm text-slate-400">{member.user.email}</p>
                    <p className="text-xs text-slate-500 mt-1 uppercase">
                      {member.user.role === 'sales_rep' ? 'Sales Executive' : member.user.role === 'manager' ? 'Sales Director' : member.user.role === 'cso' ? 'Chief Sales Officer' : 'Admin'}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-slate-800 rounded-lg p-3">
                    <p className="text-xs text-slate-400">Total Tasks</p>
                    <p className="text-2xl font-bold text-white">{member.stats.total_tasks}</p>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-3">
                    <p className="text-xs text-slate-400">Pending</p>
                    <p className="text-2xl font-bold text-amber-400">{member.stats.pending_tasks}</p>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-3">
                    <p className="text-xs text-slate-400">Total Leads</p>
                    <p className="text-2xl font-bold text-green-400">{member.stats.total_leads}</p>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-3">
                    <p className="text-xs text-slate-400">Contacts</p>
                    <p className="text-2xl font-bold text-blue-400">{member.stats.total_contacts}</p>
                  </div>
                </div>

                {member.recent_activities && member.recent_activities.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">
                      Recent Activities
                    </p>
                    <div className="space-y-2">
                      {member.recent_activities.slice(0, 3).map((activity) => (
                        <div key={activity.id} className="text-xs text-slate-400">
                          • {activity.description}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activities */}
      {stats.recent_activities.length > 0 && (
        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6" data-testid="recent-activities-report">
          <h2 className="text-xl font-semibold text-white mb-4">Recent Team Activities</h2>
          <div className="space-y-3">
            {stats.recent_activities.map((activity) => (
              <div
                key={activity.id}
                className="flex items-start gap-3 pb-3 border-b border-slate-800 last:border-0"
                data-testid="activity-item"
              >
                <Activity size={16} className="text-slate-400 mt-1" />
                <div className="flex-1">
                  <p className="text-sm text-slate-300" data-testid="activity-description">{activity.description}</p>
                  <p className="text-xs text-slate-500 mt-1" data-testid="activity-timestamp">
                    {new Date(activity.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
